import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../../lib/api";
import { Card, Btn, Input, Select } from "../../components/UI";
import { partyPill } from "../PoliticalIntelligence";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, TrendingUp, TrendingDown, Minus, Megaphone, Newspaper,
  ExternalLink, Swords, RefreshCw, Eye, HandHeart, Images, Radar, Settings2, X, Sparkles,
  AlertTriangle, Bell, Check, DollarSign, UserCog,
} from "lucide-react";
import { EditOpponentsModal } from "./EditOpponentsModal";

const AD_CHANNELS = ["Connected TV", "Broadcast TV", "Radio", "Digital / Display", "Online Video", "Paid Social", "Direct Mail", "Out-of-Home", "Other"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MomIcon = ({ d }) => d === "rising" ? <TrendingUp className="w-3.5 h-3.5 text-emerald-300" />
  : d === "falling" ? <TrendingDown className="w-3.5 h-3.5 text-rose-300" /> : <Minus className="w-3.5 h-3.5 text-slate-500" />;

const LiveAdCard = ({ ad }) => (
  <a href={ad.landing_page || undefined} target="_blank" rel="noreferrer"
    className="block rounded-md bg-white/[0.03] border border-white/5 p-2 text-xs hover:border-cyan-400/30 transition-colors"
    data-testid={`live-ad-${ad.ad_key}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-slate-200 truncate font-medium">{ad.advertiser || "Unknown advertiser"}</span>
      <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-cyan-400/25 text-cyan-300 uppercase shrink-0">{ad.platform}</span>
    </div>
    {(ad.message || ad.title) && <div className="text-slate-400 mt-0.5 line-clamp-2">{ad.message || ad.title}</div>}
    <div className="text-[10px] text-slate-600 mt-1 flex items-center gap-2">
      <span className="inline-flex items-center gap-1"><Eye className="w-2.5 h-2.5" /> {Number(ad.impressions || 0).toLocaleString()}</span>
      {ad.days_running ? <span>{ad.days_running}d running</span> : null}
      {ad.last_seen ? <span>last seen {ad.last_seen}</span> : null}
    </div>
  </a>
);

export const OppositionTab = ({ id, data }) => {
  const navigate = useNavigate();
  const [opp, setOpp] = useState(null);
  const [form, setForm] = useState({ subject_name: "", date: new Date().toISOString().slice(0, 10), channel: "Connected TV", est_spend: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [syncingAds, setSyncingAds] = useState(false);
  const [syncingMeta, setSyncingMeta] = useState(false);
  const [syncingGoogle, setSyncingGoogle] = useState(false);
  const [checkingAlerts, setCheckingAlerts] = useState(false);
  const [pacsOpen, setPacsOpen] = useState(false);
  const [oppsOpen, setOppsOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertConfig, setAlertConfig] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/political-intelligence/candidates/${id}/opposition`).then(r => setOpp(r.data))
      .catch(e => toast.error(errMsg(e, "Could not load opposition data")));
    api.get(`/political-intelligence/candidates/${id}/pac-alerts?limit=15`).then(r => setAlerts(r.data.alerts || [])).catch(() => {});
    api.get(`/political-intelligence/candidates/${id}/pac-alert-config`).then(r => setAlertConfig(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const names = [data.candidate.candidate_name, ...(data.candidate.opponents || []).map(o => o.name)];

  const syncAds = async () => {
    setSyncingAds(true);
    try {
      const r = await api.post(`/political-intelligence/candidates/${id}/opposition/sync-ads`);
      const total = Object.values(r.data.results || {}).reduce((s, x) => s + (x.synced || 0), 0);
      toast.success(`Live ads synced — ${total} ads cached${r.data.credits_remaining != null ? ` · ${r.data.credits_remaining} AdLibrary credits left` : ""}`);
      (r.data.errors || []).forEach(e => toast.warning(e));
      load();
    } catch (e) { toast.error(errMsg(e, "Live ad sync failed")); }
    finally { setSyncingAds(false); }
  };

  const syncMeta = async () => {
    setSyncingMeta(true);
    try {
      const r = await api.post(`/political-intelligence/candidates/${id}/opposition/sync-meta-ads`);
      const total = (r.data.results || []).reduce((s, x) => s + (x.active_count || 0), 0);
      const checked = r.data.subjects_checked || 0;
      toast.success(`Meta Ad Library synced — ${total.toLocaleString()} active political ads across ${checked} subjects`);
      (r.data.errors || []).forEach(e => toast.warning(e));
      load();
    } catch (e) { toast.error(errMsg(e, "Meta ad sync failed")); }
    finally { setSyncingMeta(false); }
  };

  const syncGoogle = async () => {
    setSyncingGoogle(true);
    try {
      const r = await api.post(`/political-intelligence/candidates/${id}/opposition/sync-google-ads`);
      const checked = r.data.subjects_checked || 0;
      toast.success(`Google Transparency synced — deep links refreshed for ${checked} subject${checked === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Google ad sync failed")); }
    finally { setSyncingGoogle(false); }
  };

  const checkAlerts = async () => {
    setCheckingAlerts(true);
    try {
      const r = await api.post(`/political-intelligence/candidates/${id}/pac-alerts/check`);
      const newN = (r.data.alerts || []).length;
      const checked = r.data.committees_checked || 0;
      if (newN > 0) toast.success(`⚠️ ${newN} new PAC alert${newN === 1 ? "" : "s"} across ${checked} committee${checked === 1 ? "" : "s"}`);
      else toast.success(`No new alerts — ${checked} committee${checked === 1 ? "" : "s"} within threshold`);
      load();
    } catch (e) { toast.error(errMsg(e, "PAC alert check failed")); }
    finally { setCheckingAlerts(false); }
  };

  const dismissAlert = async (aid) => {
    try {
      await api.post(`/political-intelligence/candidates/${id}/pac-alerts/${aid}/read`);
      setAlerts(a => a.map(x => x.id === aid ? { ...x, read: true } : x));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setAuto = async (enabled, cap) => {
    try {
      await api.put(`/political-intelligence/candidates/${id}/auto-ads`, { enabled, daily_credit_cap: cap ?? null });
      toast.success(enabled ? (cap ? `Daily credit cap set to ${cap}` : "Auto-sync on — opponent ads refresh daily") : "Auto-sync off");
      load();
    } catch (e) { toast.error(errMsg(e, "Could not update auto-sync")); }
  };

  const addActivity = async () => {
    if (!form.subject_name) { toast.error("Pick which candidate the ad belongs to"); return; }
    setSaving(true);
    try {
      await api.post(`/political-intelligence/candidates/${id}/ad-activity`, {
        ...form, est_spend: form.est_spend ? parseFloat(form.est_spend) : null, note: form.note || null,
      });
      toast.success("Ad activity logged");
      setForm({ ...form, est_spend: "", note: "" });
      load();
    } catch (e) { toast.error(errMsg(e, "Could not log ad activity")); }
    finally { setSaving(false); }
  };

  const removeActivity = async (aid) => {
    try {
      await api.delete(`/political-intelligence/candidates/${id}/ad-activity/${aid}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Could not delete entry")); }
  };

  if (!opp) return <div className="p-8 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2 text-cyan-300" />Loading opposition intelligence…</div>;

  return (
    <div className="space-y-4" data-testid="opposition-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-500 flex items-center gap-2 max-w-2xl">
          <Swords className="w-3.5 h-3.5 text-cyan-300/70 shrink-0" /> {opp.note}
          {opp.live_ads_synced_at && <span className="text-slate-600">· Ads synced {opp.live_ads_synced_at.slice(0, 16).replace("T", " ")} UTC</span>}
          {opp.auto_ads?.last_run_date && <span className="text-slate-600" data-testid="auto-run-info">· Auto-run {opp.auto_ads.last_run_date}{opp.auto_ads.last_result?.capped ? " (credit cap hit)" : ""}</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Btn onClick={syncAds} disabled={syncingAds} data-testid="sync-live-ads-btn">
            {syncingAds ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync live ads
          </Btn>
          <Btn onClick={syncMeta} disabled={syncingMeta} data-testid="sync-meta-ads-btn" variant="secondary">
            {syncingMeta ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />} Sync Meta (free)
          </Btn>
          <Btn onClick={syncGoogle} disabled={syncingGoogle} data-testid="sync-google-ads-btn" variant="secondary">
            {syncingGoogle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />} Sync Google (free)
          </Btn>
          <Btn onClick={checkAlerts} disabled={checkingAlerts} data-testid="check-pac-alerts-btn" variant="secondary">
            {checkingAlerts ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />} Check PAC alerts
          </Btn>
          <button onClick={() => setPacsOpen(true)} data-testid="linked-pacs-btn"
            className="text-xs px-3 py-2 rounded-full border border-white/15 text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1.5">
            <Settings2 className="w-3.5 h-3.5" /> Linked PACs ({(opp.linked_pacs || []).length})
          </button>
          <button onClick={() => setOppsOpen(true)} data-testid="edit-opponents-btn"
            className="text-xs px-3 py-2 rounded-full border border-white/15 text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1.5">
            <UserCog className="w-3.5 h-3.5" /> Edit opponents
            {(data.candidate.opponents || []).length > 0 && (
              <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${(data.candidate.opponents || []).every(o => o.fec_candidate_id) ? "bg-emerald-400/20 text-emerald-200" : "bg-amber-400/20 text-amber-200"}`}>
                {(data.candidate.opponents || []).filter(o => o.fec_candidate_id).length}/{(data.candidate.opponents || []).length} FEC-linked
              </span>
            )}
          </button>
          <button onClick={() => setAuto(!opp.auto_ads?.enabled)} data-testid="auto-ads-toggle"
            className={`text-xs px-3 py-2 rounded-full border transition-colors ${opp.auto_ads?.enabled ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-white/15 text-slate-400 hover:text-white"}`}>
            Auto daily: {opp.auto_ads?.enabled ? "On" : "Off"}
          </button>
          <button onClick={() => navigate(`/political-intelligence/${id}/photos`)} data-testid="photo-gallery-btn"
            className="text-xs px-3 py-2 rounded-full border border-white/15 text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1.5">
            <Images className="w-3.5 h-3.5" /> Photo gallery
          </button>
          {opp.auto_ads?.enabled && (
            <div className="flex items-center gap-1 text-[10px] text-slate-500">
              cap
              <input type="number" min="1" max="200" defaultValue={opp.auto_ads.daily_credit_cap} data-testid="auto-ads-cap-input"
                onBlur={e => { const v = parseInt(e.target.value); if (v && v !== opp.auto_ads.daily_credit_cap) setAuto(true, v); }}
                className="w-14 bg-slate-900/80 border border-white/15 rounded-md px-2 py-1 text-xs text-slate-300 focus:outline-none focus:border-cyan-400/60" />
              credits/day
            </div>
          )}
        </div>
      </div>

      <Card data-testid="ad-activity-form">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone className="w-4 h-4 text-amber-300" />
          <h3 className="font-display text-lg font-bold">Log ad activity</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Record ads your team spots on air, online or in mailboxes. Volunteers can also submit via the volunteer link (Share button above).</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Select label="Candidate" value={form.subject_name} onChange={e => setForm({ ...form, subject_name: e.target.value })} data-testid="ad-subject-select">
            <option value="">— pick —</option>
            {names.map(n => <option key={n} value={n}>{n}</option>)}
          </Select>
          <Input label="Date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="ad-date-input" />
          <Select label="Channel" value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })} data-testid="ad-channel-select">
            {AD_CHANNELS.map(c => <option key={c}>{c}</option>)}
          </Select>
          <Input label="Est. spend $ (optional)" type="number" value={form.est_spend} onChange={e => setForm({ ...form, est_spend: e.target.value })} placeholder="10000" data-testid="ad-spend-input" />
          <Input label="Note (optional)" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Attack ad on taxes…" data-testid="ad-note-input" />
        </div>
        <div className="mt-3">
          <Btn onClick={addActivity} disabled={saving} data-testid="log-ad-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Log activity
          </Btn>
        </div>
      </Card>

      {(alerts || []).length > 0 && (
        <Card data-testid="pac-alerts-panel" className="border-rose-400/40 bg-rose-400/[0.03]">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-4 h-4 text-rose-300" />
            <h3 className="font-display text-lg font-bold text-white">PAC spend alerts</h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-400/20 text-rose-200 font-semibold">
              {alerts.filter(a => !a.read).length} unread
            </span>
            <button onClick={() => setConfigOpen(true)} data-testid="pac-alert-config-btn"
              className="ml-auto text-[10px] text-slate-400 hover:text-white inline-flex items-center gap-1">
              <Settings2 className="w-3 h-3" /> Threshold ${(alertConfig?.config?.threshold_usd || 50000).toLocaleString()} · {alertConfig?.config?.window_days || 7}d
            </button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="pac-alerts-list">
            {alerts.slice(0, 10).map((a) => (
              <div key={a.id} className={`p-3 rounded-lg border ${a.read ? "border-white/5 bg-slate-900/40 opacity-60" : "border-rose-400/30 bg-rose-400/[0.05]"}`}
                   data-testid={`pac-alert-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <DollarSign className="w-4 h-4 text-rose-300 shrink-0" />
                      <span className="text-white font-semibold truncate">{a.committee_name}</span>
                      {a.support_oppose === "O" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-400/20 text-rose-200">OPPOSING</span>}
                      {a.support_oppose === "S" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-400/20 text-emerald-200">SUPPORTING</span>}
                    </div>
                    <div className="text-sm text-slate-200 mt-1">
                      <b className="text-rose-200">${(a.delta_usd || 0).toLocaleString()}</b> new spend in the last {a.window_days}d
                      <span className="text-slate-500"> · threshold ${(a.threshold_usd || 0).toLocaleString()}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">
                      Total: ${(a.cumulative_total || 0).toLocaleString()} · on {(a.subjects || []).join(", ") || "your race"} · via FEC Schedule E · {(a.alert_ts || "").slice(0, 10)}
                    </div>
                  </div>
                  {!a.read && (
                    <button onClick={() => dismissAlert(a.id)} data-testid={`pac-alert-dismiss-${a.id}`}
                      className="shrink-0 text-slate-500 hover:text-emerald-300" title="Mark read">
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {alertConfig?.last_check && (
            <div className="mt-3 text-[10px] text-slate-500">
              Last check: {alertConfig.last_check.slice(0, 16).replace("T", " ")} UTC · runs daily at 6 AM ET
            </div>
          )}
        </Card>
      )}

      {(opp.meta_pac_activity || []).length > 0 && (
        <Card data-testid="meta-pac-activity-strip">
          <div className="flex items-center gap-2 mb-2">
            <Radar className="w-4 h-4 text-fuchsia-300" />
            <h3 className="font-display text-lg font-bold">Linked PACs · Meta activity</h3>
            <span className="text-[10px] text-slate-500 ml-auto">{opp.meta_ads_synced_at ? `synced ${opp.meta_ads_synced_at.slice(0, 16).replace("T", " ")} UTC` : "not synced yet"}</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">Count of active political ads Meta is running from PACs/committees you&apos;ve linked to this race.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {opp.meta_pac_activity.map(p => (
              <a key={p.name} href={p.source_url} target="_blank" rel="noreferrer"
                 className="block rounded-md bg-fuchsia-400/[0.05] border border-fuchsia-400/25 p-2 text-xs hover:border-fuchsia-400/50 group"
                 data-testid={`meta-pac-${p.name}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-200 truncate">{p.name}</span>
                  <span className="text-fuchsia-200 font-semibold shrink-0">{p.active_count != null ? p.active_count.toLocaleString() : "—"}</span>
                </div>
                <div className="text-[10px] text-slate-600 mt-0.5">
                  {p.fetched_at ? p.fetched_at.slice(0, 10) : ""} · Meta Ad Library
                </div>
              </a>
            ))}
          </div>
        </Card>
      )}

      {(opp.google_pac_activity || []).length > 0 && (
        <Card data-testid="google-pac-activity-strip">
          <div className="flex items-center gap-2 mb-2">
            <Radar className="w-4 h-4 text-amber-300" />
            <h3 className="font-display text-lg font-bold">Linked PACs · Google activity</h3>
            <span className="text-[10px] text-slate-500 ml-auto">{opp.google_ads_synced_at ? `synced ${opp.google_ads_synced_at.slice(0, 16).replace("T", " ")} UTC` : "not synced yet"}</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">One-click deep-links to Google Ads Transparency Center for every linked PAC / committee.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {opp.google_pac_activity.map(p => (
              <a key={p.name} href={p.source_url} target="_blank" rel="noreferrer"
                 className="block rounded-md bg-amber-400/[0.05] border border-amber-400/25 p-2 text-xs hover:border-amber-400/50 group"
                 data-testid={`google-pac-${p.name}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-200 truncate">{p.name}</span>
                  <span className="text-amber-200 font-semibold shrink-0">{p.active_count != null ? p.active_count.toLocaleString() : "↗"}</span>
                </div>
                <div className="text-[10px] text-slate-600 mt-0.5">
                  {p.fetched_at ? p.fetched_at.slice(0, 10) : ""} · Google Transparency
                </div>
              </a>
            ))}
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {opp.columns.map((col) => (
          <Card key={col.name} className={col.is_mine ? "border-cyan-400/30" : ""} data-testid={`opposition-col-${col.name}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-display font-bold flex items-center gap-2">
                  {col.name}
                  {col.is_mine && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 uppercase">You</span>}
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border inline-block mt-1 ${partyPill(col.party)}`}>{col.party}</span>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl font-bold text-white">{col.avg != null ? `${col.avg}%` : "—"}</div>
                {col.momentum && (
                  <div className="text-xs text-slate-400 inline-flex items-center gap-1">
                    <MomIcon d={col.momentum.direction} /> {col.momentum.delta > 0 ? "+" : ""}{col.momentum.delta}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                <Eye className="w-3 h-3" /> Live ads · AdLibrary <span className="text-slate-700">({(col.live_ads || []).length})</span>
              </div>
              {(col.live_ads || []).length === 0 ? (
                <div className="text-xs text-slate-600">No live ads cached — hit &quot;Sync live ads&quot; to pull from Meta/Google/TikTok.</div>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {col.live_ads.map(ad => <LiveAdCard key={ad.ad_key} ad={ad} />)}
                </div>
              )}
            </div>

            {col.meta_activity && (
              <div className="mt-4" data-testid={`meta-activity-${col.name}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                  <Radar className="w-3 h-3 text-fuchsia-300" /> Meta Ad Library
                </div>
                <a href={col.meta_activity.source_url} target="_blank" rel="noreferrer"
                   className="block rounded-md bg-fuchsia-400/[0.05] border border-fuchsia-400/25 p-2 text-xs hover:border-fuchsia-400/50 transition-colors group"
                   data-testid={`meta-activity-link-${col.name}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-300 group-hover:text-fuchsia-200">
                      {col.meta_activity.active_count != null
                        ? <><b className="text-fuchsia-200">{col.meta_activity.active_count.toLocaleString()}</b> active political ads</>
                        : <>Open live Meta Ad Library view <span className="text-slate-500">↗</span></>}
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-500 group-hover:text-fuchsia-300 shrink-0" />
                  </div>
                  {col.meta_activity.fetched_at && (
                    <div className="text-[10px] text-slate-600 mt-1">
                      Checked {col.meta_activity.fetched_at.slice(0, 16).replace("T", " ")} UTC
                      {col.meta_activity.note ? ` · ${col.meta_activity.note.slice(0, 100)}` : ""}
                    </div>
                  )}
                </a>
              </div>
            )}

            {col.google_activity && (
              <div className="mt-3" data-testid={`google-activity-${col.name}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                  <Radar className="w-3 h-3 text-amber-300" /> Google Transparency
                </div>
                <a href={col.google_activity.source_url} target="_blank" rel="noreferrer"
                   className="block rounded-md bg-amber-400/[0.05] border border-amber-400/25 p-2 text-xs hover:border-amber-400/50 transition-colors group"
                   data-testid={`google-activity-link-${col.name}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-300 group-hover:text-amber-200">
                      {col.google_activity.active_count != null
                        ? <><b className="text-amber-200">{col.google_activity.active_count.toLocaleString()}</b> advertisers listed</>
                        : <>Open live Google Transparency view <span className="text-slate-500">↗</span></>}
                    </span>
                    <ExternalLink className="w-3 h-3 text-slate-500 group-hover:text-amber-300 shrink-0" />
                  </div>
                  {col.google_activity.fetched_at && (
                    <div className="text-[10px] text-slate-600 mt-1">
                      Checked {col.google_activity.fetched_at.slice(0, 16).replace("T", " ")} UTC
                    </div>
                  )}
                </a>
              </div>
            )}

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1"><Megaphone className="w-3 h-3" /> Team & volunteer log</div>
              {col.ad_log.length === 0 && col.ad_mentions.length === 0 && (
                <div className="text-xs text-slate-600">Nothing logged or detected yet.</div>
              )}
              <div className="space-y-1.5">
                {col.ad_log.map(a => (
                  <div key={a.id} className="flex items-start gap-2 rounded-md bg-white/[0.03] border border-white/5 p-2 text-xs" data-testid={`ad-log-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <span className="text-white">{a.channel}</span>
                      <span className="text-slate-500"> · {a.date}{a.est_spend ? ` · ~$${Number(a.est_spend).toLocaleString()}` : ""}{a.location ? ` · ${a.location}` : ""}</span>
                      {a.source === "volunteer" && (
                        <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 uppercase inline-flex items-center gap-0.5">
                          <HandHeart className="w-2.5 h-2.5" /> volunteer{a.reporter_name ? ` · ${a.reporter_name}` : ""}
                        </span>
                      )}
                      {a.note && <div className="text-slate-400 truncate">{a.note}</div>}
                      {a.photo_url && (
                        <a href={`${BACKEND}${a.photo_url}`} target="_blank" rel="noreferrer" data-testid={`sighting-photo-${a.id}`}>
                          <img src={`${BACKEND}${a.photo_url}`} alt="volunteer sighting" className="mt-1.5 rounded-md max-h-24 border border-white/10" />
                        </a>
                      )}
                    </div>
                    <button onClick={() => removeActivity(a.id)} className="text-slate-600 hover:text-rose-300 shrink-0" data-testid={`delete-ad-${a.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {col.ad_mentions.map(n => (
                  <a key={n.key} href={n.link} target="_blank" rel="noreferrer"
                    className="block rounded-md bg-amber-400/[0.05] border border-amber-400/20 p-2 text-xs group" data-testid={`ad-mention-${n.key}`}>
                    <span className="text-[9px] uppercase text-amber-300 tracking-wider">Detected in headlines</span>
                    <div className="text-slate-300 group-hover:text-amber-200 leading-snug">{n.title}</div>
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1"><Newspaper className="w-3 h-3" /> Latest headlines</div>
              {col.news.length === 0 ? (
                <div className="text-xs text-slate-600">No headlines yet — run a sync in the Live Feeds tab.</div>
              ) : (
                <div className="space-y-1.5">
                  {col.news.slice(0, 4).map(n => (
                    <a key={n.key} href={n.link} target="_blank" rel="noreferrer" className="block group">
                      <div className="text-xs text-slate-300 group-hover:text-cyan-300 leading-snug flex items-start gap-1">
                        <span className="flex-1">{n.title}</span>
                        <ExternalLink className="w-2.5 h-2.5 text-slate-700 shrink-0 mt-0.5 group-hover:text-cyan-300" />
                      </div>
                      <div className="text-[9px] text-slate-600">{n.source}{n.published_at ? ` · ${n.published_at.slice(0, 10)}` : ""}</div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {pacsOpen && (
        <LinkedPacsModal
          id={id}
          initial={opp.linked_pacs || []}
          onClose={() => setPacsOpen(false)}
          onSaved={() => { setPacsOpen(false); load(); }}
        />
      )}

      {oppsOpen && (
        <EditOpponentsModal
          id={id}
          initial={data.candidate.opponents || []}
          onClose={() => setOppsOpen(false)}
          onSaved={() => { setOppsOpen(false); window.location.reload(); }}
        />
      )}

      {configOpen && (
        <PacAlertConfigModal
          id={id}
          initial={alertConfig?.config}
          onClose={() => setConfigOpen(false)}
          onSaved={() => { setConfigOpen(false); load(); }}
        />
      )}
    </div>
  );
};

function PacAlertConfigModal({ id, initial, onClose, onSaved }) {
  const [threshold, setThreshold] = useState(initial?.threshold_usd ?? 50000);
  const [windowDays, setWindowDays] = useState(initial?.window_days ?? 7);
  const [enabled, setEnabled] = useState(initial?.enabled !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/political-intelligence/candidates/${id}/pac-alert-config`, {
        threshold_usd: Number(threshold), window_days: Number(windowDays), enabled,
      });
      toast.success("Alert threshold saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Could not save threshold")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="pac-alert-config-modal">
      <div className="bg-slate-950 border border-white/10 rounded-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Opposition</div>
            <h3 className="font-display text-lg font-bold text-white">PAC spend alerts</h3>
          </div>
          <button onClick={onClose} data-testid="pac-config-close"><X className="w-5 h-5 text-slate-400 hover:text-white" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-slate-400 leading-relaxed">
            When any PAC on FEC Schedule E spends more than <b className="text-white">${Number(threshold).toLocaleString()}</b> in a rolling <b className="text-white">{windowDays}-day</b> window on your race, we&apos;ll notify the Chief of Staff and drop it in the daily brief.
          </p>
          <div>
            <label className="text-xs text-slate-400 block mb-1.5">Threshold (USD)</label>
            <input type="number" value={threshold} onChange={e => setThreshold(e.target.value)} min="0" step="1000"
              data-testid="pac-config-threshold"
              className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1.5">Window (days)</label>
            <input type="number" value={windowDays} onChange={e => setWindowDays(e.target.value)} min="1" max="90"
              data-testid="pac-config-window"
              className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} data-testid="pac-config-enabled" />
            <span>Enable daily auto-check at 6 AM ET</span>
          </label>
          <div className="flex justify-end gap-2 pt-3 border-t border-white/5">
            <button onClick={onClose} className="text-sm text-slate-400 hover:text-white px-3 py-2">Cancel</button>
            <Btn onClick={save} disabled={saving} data-testid="pac-config-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function LinkedPacsModal({ id, initial, onClose, onSaved }) {
  const [rows, setRows] = useState(initial.length ? initial : [{ name: "" }]);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);

  const update = (i, val) => setRows(rs => rs.map((r, idx) => idx === i ? { name: val } : r));
  const removeRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const addRow = () => setRows(rs => [...rs, { name: "" }]);

  const addSuggestion = (name) => {
    const clean = (name || "").trim();
    if (!clean) return;
    setRows(rs => {
      const exists = rs.some(r => (r.name || "").trim().toLowerCase() === clean.toLowerCase());
      if (exists) return rs;
      const withoutEmpty = rs.filter(r => (r.name || "").trim());
      return [...withoutEmpty, { name: clean }];
    });
  };

  const runSuggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/political-intelligence/candidates/${id}/suggest-pacs?use_ai=true`);
      setSuggestions(data);
      const total = (data.suggestions?.length || 0) + (data.generic?.length || 0) + (data.ai_enrichment?.length || 0);
      toast.success(`${total} PAC${total === 1 ? "" : "s"} suggested — pick which to attach`);
    } catch (e) { toast.error(errMsg(e, "Could not fetch suggestions")); }
    finally { setSuggesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = rows.map(r => (r.name || "").trim()).filter(Boolean);
      const { data } = await api.put(`/political-intelligence/candidates/${id}/linked-pacs`, { pacs: clean });
      toast.success(`Linked ${data.linked_pacs.length} PAC${data.linked_pacs.length === 1 ? "" : "s"}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Could not save PACs")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="linked-pacs-modal">
      <div className="bg-slate-950 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Opposition</div>
            <h3 className="font-display text-lg font-bold text-white">Linked PACs & committees</h3>
          </div>
          <button onClick={onClose} data-testid="linked-pacs-close"><X className="w-5 h-5 text-slate-400 hover:text-white" /></button>
        </div>
        <div className="p-6 space-y-3 overflow-y-auto">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <p className="text-xs text-slate-400 leading-relaxed flex-1 min-w-[240px]">
              Paste page names of PACs or independent committees running ads for/against your race. We&apos;ll pull their active-ad count from Meta Ad Library alongside the candidate columns.
            </p>
            <Btn onClick={runSuggest} disabled={suggesting} variant="secondary" data-testid="pac-suggest-btn">
              {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Suggest from FEC + AI
            </Btn>
          </div>

          {suggestions && (
            <div className="rounded-lg border border-cyan-400/20 bg-cyan-400/[0.03] p-3 space-y-3" data-testid="pac-suggestions-panel">
              {(suggestions.suggestions || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-medium mb-2">FEC Independent Expenditures ({suggestions.suggestions.length})</div>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {suggestions.suggestions.map((s, i) => (
                      <div key={s.committee_id || s.name} className="flex items-center justify-between gap-2 text-xs" data-testid={`pac-suggestion-fec-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-200 truncate">{s.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">
                            ${Math.round(s.total_spend || 0).toLocaleString()} spent
                            {s.support_oppose === "O" && <span className="text-rose-300 ml-1">· opposing</span>}
                            {s.support_oppose === "S" && <span className="text-emerald-300 ml-1">· supporting</span>}
                            {(s.subjects_spent_on || []).length > 0 && <span className="text-slate-600"> · on {s.subjects_spent_on.join(", ")}</span>}
                          </div>
                        </div>
                        <button onClick={() => addSuggestion(s.name)} data-testid={`pac-add-fec-${i}`}
                          className="shrink-0 text-[10px] px-2 py-1 rounded-full border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10">
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(suggestions.ai_enrichment || []).length > 0 && (
                <div className="pt-2 border-t border-cyan-400/10">
                  <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-medium mb-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> AI enrichment ({suggestions.ai_enrichment.length})
                  </div>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {suggestions.ai_enrichment.map((s, i) => (
                      <div key={s.name} className="flex items-center justify-between gap-2 text-xs" data-testid={`pac-suggestion-ai-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-200 truncate">{s.name}</div>
                          <div className="text-[10px] text-slate-500 truncate italic">{s.reason}</div>
                        </div>
                        <button onClick={() => addSuggestion(s.name)} data-testid={`pac-add-ai-${i}`}
                          className="shrink-0 text-[10px] px-2 py-1 rounded-full border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10">
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(suggestions.generic || []).length > 0 && (
                <div className="pt-2 border-t border-cyan-400/10">
                  <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-medium mb-2">National PACs by office</div>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.generic.map((s, i) => (
                      <button key={s.name} onClick={() => addSuggestion(s.name)} data-testid={`pac-add-generic-${i}`}
                        className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 inline-flex items-center gap-1">
                        <Plus className="w-2.5 h-2.5" /> {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {((suggestions.suggestions || []).length === 0
                && (suggestions.ai_enrichment || []).length === 0
                && (suggestions.generic || []).length === 0) && (
                <div className="text-xs text-slate-500 italic">No suggestions found — try adding the candidate&apos;s FEC ID first.</div>
              )}
            </div>
          )}

          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`pac-row-${i}`}>
              <input value={r.name || ""} onChange={e => update(i, e.target.value)}
                placeholder="e.g. Senate Majority PAC"
                data-testid={`pac-input-${i}`}
                className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none" />
              <button onClick={() => removeRow(i)} data-testid={`pac-remove-${i}`} className="text-slate-500 hover:text-rose-400">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button onClick={addRow} data-testid="pac-add-row" className="text-xs text-cyan-300 hover:underline inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add another PAC
          </button>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} className="text-sm text-slate-400 hover:text-white px-3 py-2">Cancel</button>
          <Btn onClick={save} disabled={saving} data-testid="pac-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save PACs
          </Btn>
        </div>
      </div>
    </div>
  );
}
